import React, { useState } from 'react';
import { X, FileText, Calendar, DollarSign, Check } from 'lucide-react';
import ApiService from '../services/ApiService';

const GenerateInvoiceModal = ({ workOrder, onClose, onInvoiceGenerated }) => {
  const [formData, setFormData] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    additionalDescription: '',
    markAsInvoiced: true
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const result = await ApiService.generateInvoiceFromWorkOrder(workOrder.id, formData);
      
      if (onInvoiceGenerated) {
        onInvoiceGenerated(result);
      }
      
      onClose();
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('Error generating invoice: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-600" />
              Generate Invoice
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Work Order Summary */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-blue-800 mb-2">Work Order Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-blue-700 font-medium">Work Order:</span>
                <span className="ml-2 text-gray-900">{workOrder.workOrderNumber}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Client:</span>
                <span className="ml-2 text-gray-900">{workOrder.clientName}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Amount:</span>
                <span className="ml-2 text-gray-900 font-semibold">${workOrder.invoiceAmount.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-blue-700 font-medium">Status:</span>
                <span className="ml-2 text-gray-900">{workOrder.status}</span>
              </div>
              {workOrder.location && (
                <div className="md:col-span-2">
                  <span className="text-blue-700 font-medium">Location:</span>
                  <span className="ml-2 text-gray-900">{workOrder.location}</span>
                </div>
              )}
              {workOrder.description && (
                <div className="md:col-span-2">
                  <span className="text-blue-700 font-medium">Description:</span>
                  <span className="ml-2 text-gray-900">{workOrder.description}</span>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Invoice Date *
                </label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Due Date *
                </label>
                <input
                  type="date"
                  name="dueDate"
                  value={formData.dueDate}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Description
              </label>
              <textarea
                name="additionalDescription"
                value={formData.additionalDescription}
                onChange={handleChange}
                rows={3}
                placeholder="Add any additional details for the invoice..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="markAsInvoiced"
                name="markAsInvoiced"
                checked={formData.markAsInvoiced}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="markAsInvoiced" className="ml-2 text-sm text-gray-700">
                Mark work order as "Invoiced" after generating invoice
              </label>
            </div>

            {/* Invoice Preview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">Invoice Preview</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Number:</span>
                  <span className="font-medium">{workOrder.workOrderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Client:</span>
                  <span className="font-medium">{workOrder.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-medium">{workOrder.serviceType || 'Service'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-bold text-green-600">${workOrder.invoiceAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Date:</span>
                  <span className="font-medium">{new Date(formData.invoiceDate).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Due Date:</span>
                  <span className="font-medium">{new Date(formData.dueDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isProcessing}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isProcessing}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4" />
                    Generate Invoice
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Warning for already invoiced work orders */}
          {workOrder.status === 'Invoiced' && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Work Order Already Invoiced
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>This work order has already been invoiced. Generating a new invoice may create duplicates.</p>
                    {workOrder.xeroInvoiceNumber && (
                      <p className="mt-1">Existing invoice: <span className="font-medium">{workOrder.xeroInvoiceNumber}</span></p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateInvoiceModal;

